import { z } from "zod";
import { zId, zIsoDate, zMoney, zQueryBoolean } from "./common.js";
import { parseMinorUnits, zCurrencyCode, zFxRateMicros } from "./currency.js";

// ---- dimensions: fund / campaign / appeal / package ----
export const zFund = z.object({
  id: zId,
  name: z.string(),
  code: z.string().nullable(),
  glAccount: z.string().nullable(),
  description: z.string().nullable(),
  archived: z.boolean(),
});
export type Fund = z.infer<typeof zFund>;

export const zCampaign = z.object({
  id: zId,
  name: z.string(),
  code: z.string().nullable(),
  goalAmount: zMoney.nullable(),
  startDate: zIsoDate.nullable(),
  endDate: zIsoDate.nullable(),
  description: z.string().nullable(),
  archived: z.boolean(),
});
export type Campaign = z.infer<typeof zCampaign>;

export const zAppeal = z.object({
  id: zId,
  campaignId: zId.nullable(),
  name: z.string(),
  code: z.string().nullable(),
  goalAmount: zMoney.nullable(),
  archived: z.boolean(),
});
export type Appeal = z.infer<typeof zAppeal>;

export const zPackage = z.object({
  id: zId,
  appealId: zId,
  name: z.string(),
  code: z.string().nullable(),
  archived: z.boolean(),
});
export type GiftPackage = z.infer<typeof zPackage>;

export const zUpsertDimension = z.object({
  name: z.string().trim().min(1).max(200),
  code: z.string().trim().max(50).optional(),
  glAccount: z.string().trim().max(50).optional(),
  description: z.string().max(2000).optional(),
  goalAmount: zMoney.optional(),
  startDate: zIsoDate.optional(),
  endDate: zIsoDate.optional(),
  campaignId: zId.optional(),
  appealId: zId.optional(),
  archived: z.boolean().optional(),
});

// ---- gifts ----
export const PAYMENT_METHODS = [
  "cash",
  "check",
  "card",
  "ach",
  "stock",
  "in_kind",
  "daf",
  "payroll",
  "crypto",
  "wire",
  "other",
] as const;
export const zPaymentMethod = z.enum(PAYMENT_METHODS);

export const GIFT_CHANNELS = ["online", "mail", "phone", "event", "in_person", "text", "grant", "membership", "auction", "other"] as const;
export const zGiftChannel = z.enum(GIFT_CHANNELS);

export const GIFT_TYPES = ["one_time", "pledge_payment", "recurring_payment", "matching_gift", "in_kind", "stock"] as const;
export const zGiftType = z.enum(GIFT_TYPES);

export const ACK_STATUSES = ["pending", "task_created", "sent", "not_needed"] as const;
export const zAckStatus = z.enum(ACK_STATUSES);

export const RECEIPT_STATUSES = ["not_receipted", "receipted", "reissued", "not_receiptable", "voided"] as const;
export const zReceiptStatus = z.enum(RECEIPT_STATUSES);

export const zSoftCredit = z.object({
  constituentId: zId,
  constituentName: z.string().optional(),
  amount: zMoney.nullable().describe("null = use percentage"),
  percentage: z.number().min(0).max(100).nullable(),
});
export type SoftCredit = z.infer<typeof zSoftCredit>;

export const zTribute = z.object({
  kind: z.enum(["in_honor", "in_memory"]),
  honoree: z.string().max(200),
  notifyConstituentId: zId.nullable(),
  message: z.string().max(2000).nullable(),
});
export type Tribute = z.infer<typeof zTribute>;

export const zStockDetail = z.object({
  ticker: z.string().max(10),
  shares: z.number().positive(),
  salePrice: zMoney.nullable(),
});

// ---- split allocations (CT-RM-18): one gift, one payment, one receipt — designated across funds ----
export const MAX_GIFT_ALLOCATIONS = 20;

/** One designation line of a gift. `amount` is base-currency cents. */
export const zGiftAllocation = z.object({
  fundId: zId,
  fundName: z.string().optional(),
  campaignId: zId.nullable(),
  campaignName: z.string().nullable().optional(),
  appealId: zId.nullable(),
  appealName: z.string().nullable().optional(),
  amount: zMoney,
});
export type GiftAllocation = z.infer<typeof zGiftAllocation>;

export const zGiftAllocationInput = z.object({
  fundId: zId,
  campaignId: zId.nullable().optional(),
  appealId: zId.nullable().optional(),
  amount: zMoney.refine((v) => v > 0, "Each split needs an amount above zero"),
});
export type GiftAllocationInput = z.infer<typeof zGiftAllocationInput>;

/**
 * Why a set of allocations can't be saved against a gift of `amount` cents, or null.
 * Shared so the API guard and the split editor say the same thing.
 */
export function allocationProblem(
  amount: number,
  allocations: { fundId: string; campaignId?: string | null; appealId?: string | null; amount: number }[],
  fmt: (cents: number) => string = (c) => (c / 100).toFixed(2),
): string | null {
  if (!allocations.length) return "Add at least one fund.";
  if (allocations.length > MAX_GIFT_ALLOCATIONS) return `A gift can be split across at most ${MAX_GIFT_ALLOCATIONS} designations.`;
  if (allocations.some((a) => !a.fundId)) return "Choose a fund for every split.";
  if (allocations.some((a) => !Number.isInteger(a.amount) || a.amount <= 0)) return "Each split needs an amount above zero.";
  const keys = allocations.map((a) => `${a.fundId}|${a.campaignId ?? ""}|${a.appealId ?? ""}`);
  if (new Set(keys).size !== keys.length) return "Two splits have the same fund, campaign and appeal. Combine them.";
  const total = allocations.reduce((s, a) => s + a.amount, 0);
  if (total !== amount) {
    const diff = amount - total;
    return `Splits total ${fmt(total)} but the gift is ${fmt(amount)} (${fmt(Math.abs(diff))} ${diff > 0 ? "unallocated" : "over"}).`;
  }
  return null;
}

/** "General Fund", or "General Fund + 2 more" for a split gift. */
export function designationLabel(fundNames: string[]): string {
  if (!fundNames.length) return "General Fund";
  return fundNames.length === 1 ? fundNames[0]! : `${fundNames[0]} + ${fundNames.length - 1} more`;
}

/**
 * Splits `total` cents across lines in proportion to `weights` (largest remainder), so the
 * parts are integers that add back to `total` exactly — e.g. a gift's deductible amount
 * spread over its allocations for the GL journal.
 */
export function prorateCents(total: number, weights: number[]): number[] {
  const sum = weights.reduce((s, w) => s + w, 0);
  if (!weights.length) return [];
  if (sum <= 0) return weights.map((_, i) => (i === 0 ? total : 0));
  const raw = weights.map((w) => (total * w) / sum);
  const parts = raw.map(Math.floor);
  const left = total - parts.reduce((s, p) => s + p, 0);
  // hand the leftover cents to the largest fractional parts (ties: earliest line)
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let n = 0; n < left; n++) {
    const target = order[n % order.length]!.i;
    parts[target] = parts[target]! + 1;
  }
  return parts;
}

export const zGift = z.object({
  id: zId,
  constituentId: zId,
  constituentName: z.string().optional(),
  type: zGiftType,
  amount: zMoney.describe("always the org's base currency"),
  receiptAmount: zMoney.describe("tax-deductible portion"),
  benefitAmount: zMoney.describe("quid pro quo benefit value"),
  /** multi-currency: original tender when it differs from the base currency */
  currency: z.string().nullable().optional(),
  originalAmount: zMoney.nullable().optional(),
  fxRateMicros: z.number().int().nullable().optional(),
  /** UK Gift Aid */
  giftAidEligible: z.boolean().optional(),
  giftAidClaimId: zId.nullable().optional(),
  date: zIsoDate,
  fundId: zId,
  fundName: z.string().optional(),
  campaignId: zId.nullable(),
  campaignName: z.string().nullable().optional(),
  appealId: zId.nullable(),
  appealName: z.string().nullable().optional(),
  /** every designation; one entry for an unsplit gift. fundId/campaignId/appealId mirror the first. */
  allocations: z.array(zGiftAllocation).optional(),
  packageId: zId.nullable(),
  paymentMethod: zPaymentMethod,
  channel: zGiftChannel,
  checkNumber: z.string().nullable(),
  anonymous: z.boolean(),
  ackStatus: zAckStatus,
  ackAt: z.string().nullable(),
  receiptStatus: zReceiptStatus,
  receiptNumber: z.string().nullable(),
  /** active (non-voided) per-gift receipt, for linking to its PDF */
  receiptId: zId.nullable().optional(),
  tribute: zTribute.nullable(),
  softCredits: z.array(zSoftCredit),
  matchingGiftForId: zId.nullable(),
  pledgeId: zId.nullable(),
  recurringGiftId: zId.nullable(),
  batchId: zId.nullable(),
  stock: zStockDetail.nullable(),
  inKindDescription: z.string().nullable(),
  inKindFairMarketValue: zMoney.nullable(),
  dafSponsor: z.string().nullable(),
  notes: z.string().nullable(),
  customFields: z.record(z.unknown()),
  externalId: z.string().nullable(),
  reversedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Gift = z.infer<typeof zGift>;

export const zCreateGift = z.object({
  idempotencyKey: zId.optional(),
  constituentId: zId,
  type: zGiftType.default("one_time"),
  amount: zMoney.describe("cents"),
  receiptAmount: zMoney.optional(),
  benefitAmount: zMoney.default(0),
  date: zIsoDate,
  fundId: zId,
  campaignId: zId.optional(),
  appealId: zId.optional(),
  /**
   * Split designation (CT-RM-18): 2+ lines summing exactly to `amount`. When present, the
   * first line becomes the gift's primary fundId/campaignId/appealId. Omit for a single fund.
   */
  allocations: z.array(zGiftAllocationInput).min(1).max(MAX_GIFT_ALLOCATIONS).optional(),
  packageId: zId.optional(),
  paymentMethod: zPaymentMethod.default("check"),
  channel: zGiftChannel.default("mail"),
  checkNumber: z.string().trim().max(50).optional(),
  anonymous: z.boolean().default(false),
  tribute: zTribute.nullable().optional(),
  softCredits: z.array(zSoftCredit).default([]),
  matchingGiftForId: zId.optional(),
  pledgeId: zId.optional(),
  recurringGiftId: zId.optional(),
  stock: zStockDetail.nullable().optional(),
  inKindDescription: z.string().max(2000).optional(),
  inKindFairMarketValue: zMoney.optional(),
  dafSponsor: z.string().max(200).optional(),
  notes: z.string().max(5000).optional(),
  customFields: z.record(z.unknown()).default({}),
  externalId: z.string().max(100).optional(),
  /** multi-currency (requires the org flag): server recomputes `amount` from these */
  currency: zCurrencyCode.optional(),
  originalAmount: zMoney.optional(),
  fxRateMicros: zFxRateMicros.optional(),
});
export type CreateGift = z.infer<typeof zCreateGift>;

export const zUpdateGift = zCreateGift
  .omit({ constituentId: true, currency: true, originalAmount: true, fxRateMicros: true })
  .partial()
  .extend({
    /** staff override; only while the gift is not in a Gift Aid claim */
    giftAidEligible: z.boolean().optional(),
  });

// ---- gift actions (CT-FEAT-002): void / refund are record-only reversals ----
/** POST /gifts/:id/void — a reason is mandatory and lands in the audit log. */
export const zVoidGift = z.object({
  reason: z.string().trim().min(3, "Give a reason (at least 3 characters)").max(500),
});
export type VoidGift = z.infer<typeof zVoidGift>;

/** POST /gifts/:id/refund — records a refund already made elsewhere; never calls a payment processor. */
export const zRefundGift = z.object({
  reason: z
    .string()
    .trim()
    .max(500)
    .optional()
    .transform((v) => v || undefined),
  refundedOn: zIsoDate.optional(),
});
export type RefundGift = z.infer<typeof zRefundGift>;

export const GIFT_REVERSAL_KINDS = ["void", "refund", "reversal"] as const;
export type GiftReversalKind = (typeof GIFT_REVERSAL_KINDS)[number];

export const zGiftHistoryEntry = z.object({
  id: zId,
  action: z.string(),
  summary: z.string(),
  actorEmail: z.string().nullable(),
  createdAt: z.string(),
  reason: z.string().nullable(),
});
export type GiftHistoryEntry = z.infer<typeof zGiftHistoryEntry>;

/** GET /gifts/:id/detail — everything the gift drawer shows. */
export const zGiftDetail = zGift.extend({
  batchName: z.string().nullable(),
  batchStatus: z.enum(["open", "committed", "reversed"]).nullable(),
  /** came through a payment processor (online/Stripe or a processor-billed recurring plan) */
  processorBacked: z.boolean(),
  reversal: z
    .object({ kind: z.enum(GIFT_REVERSAL_KINDS), reason: z.string().nullable(), at: z.string(), refundedOn: zIsoDate.nullable() })
    .nullable(),
  history: z.array(zGiftHistoryEntry),
  /**
   * CT-RM-32: the peer-to-peer fundraiser page / team the gift came through —
   * from the gift itself, else from its recurring plan (monthly gifts via a page).
   */
  p2p: z
    .object({
      campaignId: zId,
      campaignName: z.string(),
      pageId: zId.nullable(),
      pageTitle: z.string().nullable(),
      fundraiserName: z.string().nullable(),
      teamId: zId.nullable(),
      teamName: z.string().nullable(),
      viaRecurring: z.boolean(),
    })
    .nullable()
    .optional(),
});
export type GiftDetail = z.infer<typeof zGiftDetail>;

/** Shared copy for void/refund guards — the API throws it, the drawer shows it before the user tries. */
export function giftReversalBlock(gift: { reversedAt?: string | Date | null; batchStatus?: string | null }): string | null {
  if (gift.reversedAt) return "This gift has already been voided or refunded.";
  if (gift.batchStatus === "open") return "This gift is in an open batch. Commit the batch before voiding or refunding it.";
  return null;
}

export const PROCESSOR_REFUND_NOTE = "Refund in your payment processor, then record it here.";

/** GET /gifts query. `q` searches donor name/email, check number and amount. */
export const zGiftListQuery = z.object({
  q: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => v || undefined),
  constituentId: zId.optional(),
  fundId: zId.optional(),
  campaignId: zId.optional(),
  appealId: zId.optional(),
  batchId: zId.optional(),
  ackStatus: zAckStatus.optional(),
  from: zIsoDate.optional(),
  to: zIsoDate.optional(),
  includeReversed: zQueryBoolean.default(false),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type GiftListQuery = z.infer<typeof zGiftListQuery>;

/**
 * Reads a gift-search term as a money amount in minor units ("75.25",
 * "$75", "1,250.00" → cents); null when it isn't an unambiguous amount.
 */
export function parseGiftSearchAmount(q: string, exponent = 2): number | null {
  const raw = q.trim().replace(/^[$£€¥]\s*/, "");
  if (!raw) return null;
  return parseMinorUnits(raw, exponent);
}

/** Escapes LIKE/ILIKE wildcards (`%`, `_`) and the backslash escape char. */
export function escapeLikePattern(value: string): string {
  return value.replace(/[\\%_]/g, "\\$&");
}

// ---- pledges ----
export const PLEDGE_STATUSES = ["active", "fulfilled", "written_off", "cancelled"] as const;
export const zPledgeStatus = z.enum(PLEDGE_STATUSES);

export const zPledgeInstallment = z.object({
  id: zId,
  dueDate: zIsoDate,
  amount: zMoney,
  paidAmount: zMoney,
  status: z.enum(["due", "paid", "partial", "overdue", "written_off"]),
});
export type PledgeInstallment = z.infer<typeof zPledgeInstallment>;

export const zPledge = z.object({
  id: zId,
  constituentId: zId,
  constituentName: z.string().optional(),
  totalAmount: zMoney,
  paidAmount: zMoney,
  balance: zMoney,
  status: zPledgeStatus,
  fundId: zId,
  fundName: z.string().optional(),
  campaignId: zId.nullable(),
  appealId: zId.nullable(),
  startDate: zIsoDate,
  frequency: z.enum(["monthly", "quarterly", "semiannual", "annual", "custom"]),
  installments: z.array(zPledgeInstallment),
  writeOffAmount: zMoney,
  notes: z.string().nullable(),
  createdAt: z.string(),
});
export type Pledge = z.infer<typeof zPledge>;

export const zCreatePledge = z.object({
  constituentId: zId,
  totalAmount: zMoney,
  fundId: zId,
  campaignId: zId.optional(),
  appealId: zId.optional(),
  startDate: zIsoDate,
  frequency: z.enum(["monthly", "quarterly", "semiannual", "annual", "custom"]),
  installmentCount: z.number().int().min(1).max(360),
  customInstallments: z
    .array(z.object({ dueDate: zIsoDate, amount: zMoney }))
    .optional()
    .describe("required when frequency=custom"),
  notes: z.string().max(5000).optional(),
});
export type CreatePledge = z.infer<typeof zCreatePledge>;

// ---- recurring gifts ----
export const RECURRING_STATUSES = ["active", "paused", "cancelled", "failing", "completed"] as const;
export const zRecurringStatus = z.enum(RECURRING_STATUSES);

export const zRecurringGift = z.object({
  id: zId,
  constituentId: zId,
  constituentName: z.string().optional(),
  amount: zMoney,
  frequency: z.enum(["weekly", "monthly", "quarterly", "annual"]),
  status: zRecurringStatus,
  fundId: zId,
  campaignId: zId.nullable(),
  appealId: zId.nullable(),
  nextChargeDate: zIsoDate.nullable(),
  paymentProvider: z.string().nullable(),
  providerSubscriptionId: z.string().nullable(),
  failureCount: z.number().int(),
  startedAt: zIsoDate,
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  totalCollected: zMoney,
  history: z
    .array(z.object({ at: z.string(), change: z.string(), fromAmount: zMoney.nullable(), toAmount: zMoney.nullable() }).passthrough())
    .default([]),
  // CT-RM-26: card summary (never a card number) and dunning
  cardBrand: z.string().nullable().optional(),
  cardLast4: z.string().nullable().optional(),
  cardExpMonth: z.number().int().nullable().optional(),
  cardExpYear: z.number().int().nullable().optional(),
  dunningState: z.enum(["active", "past_due", "recovering", "recovered", "lapsed"]).optional(),
  nextRetryDate: zIsoDate.nullable().optional(),
});
export type RecurringGift = z.infer<typeof zRecurringGift>;

export const zCreateRecurringGift = z.object({
  constituentId: zId,
  amount: zMoney,
  frequency: z.enum(["weekly", "monthly", "quarterly", "annual"]),
  fundId: zId,
  campaignId: zId.optional(),
  appealId: zId.optional(),
  startDate: zIsoDate,
  paymentProvider: z.string().max(50).optional(),
  providerSubscriptionId: z.string().max(200).optional(),
});

// ---- batches ----
export const zGiftBatch = z.object({
  id: zId,
  name: z.string(),
  status: z.enum(["open", "committed", "reversed"]),
  giftCount: z.number().int(),
  totalAmount: zMoney,
  defaults: z.record(z.unknown()),
  /** control totals from the deposit slip; null on batches created before CT-RM-14 */
  expectedCount: z.number().int().nullable(),
  expectedTotal: zMoney.nullable(),
  createdById: zId,
  committedAt: z.string().nullable(),
  reversedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type GiftBatch = z.infer<typeof zGiftBatch>;

const zExpectedCount = z.number().int().min(0).max(100_000);

export const zCreateBatch = z.object({
  name: z.string().trim().min(1).max(200),
  defaults: z.record(z.unknown()).default({}),
  expectedCount: zExpectedCount.nullable().optional(),
  expectedTotal: zMoney.nullable().optional(),
});
export type CreateBatch = z.infer<typeof zCreateBatch>;

/** PATCH /batches/:id — only while the batch is open. */
export const zUpdateBatch = z
  .object({
    name: z.string().trim().min(1).max(200),
    expectedCount: zExpectedCount.nullable(),
    expectedTotal: zMoney.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type UpdateBatch = z.infer<typeof zUpdateBatch>;

/** The fields the keyboard entry grid edits on a batch row (PATCH /batches/:id/gifts/:giftId). */
export const zUpdateBatchGift = zCreateGift
  .pick({ amount: true, date: true, fundId: true, allocations: true, paymentMethod: true, checkNumber: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type UpdateBatchGift = z.infer<typeof zUpdateBatchGift>;

export interface BatchBalance {
  /** false until both expected values are set */
  hasExpected: boolean;
  balanced: boolean;
  countDiff: number | null;
  totalDiff: number | null;
  /** why commit is blocked, or null when the batch balances */
  problem: string | null;
}

/**
 * The batch control check: entered gifts must match the expected count and
 * total exactly (integer cents). Shared so the API's commit guard and the
 * web control panel can't disagree. `fmt` formats cents for the message.
 */
export function batchBalance(
  b: { expectedCount: number | null; expectedTotal: number | null; enteredCount: number; enteredTotal: number },
  fmt: (cents: number) => string,
): BatchBalance {
  if (b.expectedCount == null || b.expectedTotal == null) {
    return { hasExpected: false, balanced: false, countDiff: null, totalDiff: null, problem: "Set the expected gift count and total before committing." };
  }
  const countDiff = b.enteredCount - b.expectedCount;
  const totalDiff = b.enteredTotal - b.expectedTotal;
  if (b.enteredCount === 0) return { hasExpected: true, balanced: false, countDiff, totalDiff, problem: "Batch has no gifts." };
  if (countDiff === 0 && totalDiff === 0) return { hasExpected: true, balanced: true, countDiff, totalDiff, problem: null };
  const parts: string[] = [];
  if (countDiff !== 0) parts.push(`${Math.abs(countDiff)} ${Math.abs(countDiff) === 1 ? "gift" : "gifts"} ${countDiff > 0 ? "over" : "short"}`);
  if (totalDiff !== 0) parts.push(`${fmt(Math.abs(totalDiff))} ${totalDiff > 0 ? "over" : "short"}`);
  return {
    hasExpected: true,
    balanced: false,
    countDiff,
    totalDiff,
    problem: `Batch is out of balance (${parts.join(", ")}): entered ${b.enteredCount} ${b.enteredCount === 1 ? "gift" : "gifts"} totaling ${fmt(b.enteredTotal)}, expected ${b.expectedCount} totaling ${fmt(b.expectedTotal)}.`,
  };
}

// ---- receipts ----
export const zReceipt = z.object({
  id: zId,
  giftId: zId.nullable(),
  constituentId: zId,
  receiptNumber: z.string(),
  kind: z.enum(["per_gift", "year_end"]),
  year: z.number().int().nullable(),
  amount: zMoney,
  issuedAt: z.string(),
  voidedAt: z.string().nullable(),
  reissuedFromId: zId.nullable(),
  deliveryMethod: z.enum(["email", "print", "none"]),
  pdfKey: z.string().nullable(),
});
export type Receipt = z.infer<typeof zReceipt>;

/** POST /receipts/gift/:giftId — "email" only queues an outbound_emails row; "print" sends nothing. */
export const zIssueGiftReceipt = z.object({
  deliveryMethod: z.enum(["email", "print"]).default("print"),
});
export type IssueGiftReceipt = z.infer<typeof zIssueGiftReceipt>;

/** Display-only: a gift can be receipted on demand when it is live and has no active receipt. */
export function canIssueGiftReceipt(gift: { receiptStatus: string; reversedAt?: string | null }): boolean {
  return gift.receiptStatus === "not_receipted" && !gift.reversedAt;
}

// ---- acknowledgment rules ----
export const zAckRule = z.object({
  id: zId,
  name: z.string(),
  priority: z.number().int(),
  enabled: z.boolean(),
  minAmount: zMoney.nullable(),
  maxAmount: zMoney.nullable(),
  fundId: zId.nullable(),
  action: z.enum(["auto_email", "create_task"]),
  emailTemplateId: zId.nullable(),
  taskAssigneeId: zId.nullable(),
  taskTitleTemplate: z.string().nullable(),
});
export type AckRule = z.infer<typeof zAckRule>;

/** GET /ack-rules/preview — what recording a gift like this would trigger. Read-only. */
export const zAckPreviewQuery = z.object({
  amount: z.coerce.number().int().positive(),
  fundId: zId,
  constituentId: zId.optional(),
  anonymous: zQueryBoolean.optional(),
});
export type AckPreviewQuery = z.infer<typeof zAckPreviewQuery>;

export const zAckPreview = z.object({
  outcome: z.enum(["email", "task", "none"]),
  ruleName: z.string().nullable(),
  /** why an email rule fell back to a task, or why nothing happens */
  reason: z.enum(["anonymous", "no_rule", "no_email", "do_not_email"]).nullable(),
  toAddress: z.string().optional(),
});
export type AckPreview = z.infer<typeof zAckPreview>;

export const zUpsertAckRule = z.object({
  name: z.string().trim().min(1).max(200),
  priority: z.number().int().default(0),
  enabled: z.boolean().default(true),
  minAmount: zMoney.nullable().optional(),
  maxAmount: zMoney.nullable().optional(),
  fundId: zId.nullable().optional(),
  action: z.enum(["auto_email", "create_task"]),
  emailTemplateId: zId.nullable().optional(),
  taskAssigneeId: zId.nullable().optional(),
  taskTitleTemplate: z.string().max(300).nullable().optional(),
});
